import React, { useEffect, useRef, useState } from "react";
import {
  errorcode,
  GlobalConfiguration,
  IntervalConfiguration,
} from "../../../../utils/configurationProvider";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import CommonToast from "../../CommonToast";

import TEXT from "../../../../utils/globalText";
import { FieldForceRequestMultiTxnDeniedApproval } from "../../../../redux/features/fieldForceTransaction";
import ICONS from "../../../../assets/Icons";
import SmallPopupWithBulletPoints from "../../CommonSmallPopupWithBulletPoint";

const FieldForceTxnDeclinedPopup = ({
  handleClose,
  txnId,
  selectedRequestData,
}) => {
  const services = useSelector((state) => state.refreshToken.services);
  const dispatch = useDispatch();
  const Navigate = useNavigate();
  const isSubmittingRef = useRef(false);

  const [declineReason, setDeclineReason] = useState("");
  const [showLoader, setShowLoader] = useState(false);
  const [inputDisabled, setInputDisabled] = useState(false);

  const handleInputChange = (event) => {
    let value = event.target.value;

    value = value.replace(/[^a-zA-Z0-9\s]/g, "");
    value = value.replace(/^\s+/, "");
    value = value.replace(/\s{2,}/g, " ");
    value = value.slice(
      GlobalConfiguration.lengthCheckZero,
      GlobalConfiguration.inputMaxLength,
    );

    setDeclineReason(value);
  };

  const handleTxnMultiRejecting = () => {
    setTimeout(() => {
      toast.dismiss();
    }, IntervalConfiguration.intervalExtraLarge);
  };

  const finalize = () => {
    setShowLoader(false);
    setInputDisabled(false);
    setDeclineReason("");
    handleClose();
  };

  const showLoadingToast = (txt) => {
    CommonToast({
      text: txt,
      icon: ICONS?.LOADER_GIF,
      variant: "var(--processing)",
      buttonNeeded: false,
      btnProps: {
        enabledBgColor: "var(--primary-bg)",
        enabledTextColor: "var(--text-primary)",
        btnText: "",
        handleFunction: () => {},
        icon: "",
        btnType: "button",
        isDisabled: false,
        hoverOption: "white",
      },
    });
  };

  const handleReject = async (id, reason) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    try {
      if (
        services?.find(
          (s) => s === "AReimbursementRequest" || s === "Admin",
        ) === undefined
      ) {
        Navigate("/feature_not_assigned", {
          state: { unavailableService: "AReimbursementRequest, Admin" },
        });
        return finalize();
      }

      if (!sessionStorage.getItem("q2p_token")) {
        return finalize();
      }

      setShowLoader(true);
      setInputDisabled(true);

      showLoadingToast("Rejecting Request");
      handleTxnMultiRejecting();

      const requestIds = Array.isArray(id) ? id : [id];
      let payload;
      try {
        const result = await dispatch(
          FieldForceRequestMultiTxnDeniedApproval({
            request_ids: requestIds,
            rejection_reason: reason,
          }),
        ).unwrap();
        payload = result?.payload ?? result;
      } catch (err) {
        payload = err;
      }

      await new Promise((resolve) =>
        setTimeout(resolve, IntervalConfiguration.intervalExtraLarge),
      );

      if (payload?.status === errorcode.errorFailed) {
        CommonToast({
          text: payload?.error || "Rejection failed.",
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
          btnProps: {
            enabledBgColor: "var(--primary-bg)",
            enabledTextColor: "var(--text-primary)",
            btnText: "",
            handleFunction: () => {},
            icon: "",
            btnType: "button",
            isDisabled: false,
            hoverOption: "white",
          },
        });
        return finalize();
      }

      if (
        payload?.errors?.user_unauthorized?.[
          GlobalConfiguration.lengthCheckZero
        ]
      ) {
        Navigate("/feature_not_assigned", {
          state: { unavailableService: "AReimbursementRequest, Admin" },
        });
        return finalize();
      }

      if (!payload || payload?.status === false) {
        if (payload?.errors?.base) {
          sessionStorage.setItem(
            "ReimbursementRequestDataError",
            JSON.stringify(payload.errors.base),
          );
        }
        Navigate(
          "/field_force_transaction_dashboard/field_force_request_multi_approval/multi_approval/request_multi_transaction_error",
        );
        return finalize();
      }

      const dataToSave = payload?.requests ?? selectedRequestData ?? [];
      sessionStorage.setItem(
        "selectedReimbursementRequestTxnData",
        JSON.stringify({
          selectedSendTransactions: dataToSave,
          selectedTransactions: requestIds,
        }),
      );
      Navigate(
        "/field_force_transaction_dashboard/field_force_request_multi_approval/multi_approval/request_multi_transaction_declined",
      );
      finalize();
    } finally {
      isSubmittingRef.current = false;
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      const emojiRegex =
        /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g;
      if (emojiRegex.test(event.key)) {
        event.preventDefault();
        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();

        if (
          !inputDisabled &&
          declineReason.trim().length >=
            GlobalConfiguration.owneraccountnormalLength
        ) {
          handleReject(txnId, declineReason);
        }
      }

      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [inputDisabled, declineReason, txnId]);

  return (
    <div className="fixed top-top-base left-left-base w-full-width h-full-height z-z-index-modal bg-PopUp-BackDrop bg-opacity-dark-opacity">
      <SmallPopupWithBulletPoints
        inputAdornment={false}
        headerTitle={TEXT?.REJECT_REASON_REQ}
        headerFontWeight="semibold"
        headerFontColor="text-primary"
        textSize="font-md"
        subtext={TEXT?.PROVIDE_REJECT_REQUEST_REASON_PROMPT}
        noteText="Minimum 10 characters required."
        isTextInputField={true}
        inputPlaceholder="Reason"
        subtextwithbullet="Y"
        bulletPoints={[
          "Once rejected, a new request should be created and no transaction will be initiated.",
          "The user needs to create a new request.",
        ]}
        inputVariant={
          declineReason &&
          declineReason.length > GlobalConfiguration.accountnormalLength
            ? "verified"
            : declineReason &&
                declineReason.length < GlobalConfiguration.panLength
              ? "typing"
              : "default"
        }
        inputBgColor="var(--primary-bg)"
        inputFgColor="var(--text-secondary)"
        inputOnInput={(e) => {
          if (!showLoader) {
            handleInputChange(e);
          }
        }}
        inputValue={declineReason}
        inputFontKind="big"
        inputIsFullWidth={true}
        btnOneText={TEXT.CANCEL}
        btnOneHandleFunction={() => {
          if (!showLoader) {
            handleClose();
          }
        }}
        btnOneEnabledBgColor="var(--primary-bg)"
        btnOneOutLineColor="var(--text-primary)"
        btnOneEnabledTextColor="var(--text-primary)"
        btnOneHoverOption="white"
        btnOneIsDisabled={showLoader}
        btnTwoText={TEXT.REJECT}
        btnTwoHandleFunction={() => {
          if (
            !declineReason ||
            declineReason.length < GlobalConfiguration.panLength ||
            showLoader
          ) {
            return;
          }
          handleReject(txnId, declineReason);
        }}
        btnTwoEnabledBgColor="var(--negative-act)"
        btnTwoEnabledTextColor="var(--text-primary)"
        btnTwoIsDisabled={
          !declineReason || declineReason.length < GlobalConfiguration.panLength
        }
        btnTwoDisabledBgColor="var(--text-disabled)"
        btnTwoIsLoaderNeeded={!!showLoader}
        btnTwoIsLoading={!!showLoader}
        btnTwoHoverOption="black"
        btnTwoInActiveVariantNeeded={true}
      />
    </div>
  );
};

export default FieldForceTxnDeclinedPopup;
